import { useState, useCallback } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import type { Task, TaskResult, TaskAttempt, StudentProfile } from "../lib/taskEngine";
import { isTaskAssignedToStudent } from "../lib/taskEngine";
import { checkClassroomMembership } from "./useStudentClassrooms";
import { useStudentStats } from "./useStudentStats";

interface TaskPlayerState {
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  task: Task | null;
  result: TaskResult | null;
}

export function useTaskPlayer() {
  const { recordTaskCompletion } = useStudentStats();
  const [state, setState] = useState<TaskPlayerState>({
    isLoading: false,
    isSubmitting: false,
    error: null,
    task: null,
    result: null,
  });

  // Cargar tarea por código
  const loadTask = useCallback(
    async (code: string, studentUid?: string): Promise<Task | null> => {
      setState((s) => ({ ...s, isLoading: true, error: null, task: null }));

      try {
        const snap = await getDoc(doc(db, "bordona_tasks", code.toUpperCase().trim()));
        if (!snap.exists()) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: "Código no encontrado. Verifica que esté escrito correctamente.",
          }));
          return null;
        }

        const data = snap.data() as Task;

        if (data.expiresAt && Date.now() > data.expiresAt) {
          setState((s) => ({ ...s, isLoading: false, error: "Esta tarea ha expirado." }));
          return null;
        }

        if (data.classroomId) {
          if (!studentUid) {
            setState((s) => ({
              ...s,
              isLoading: false,
              task: data,
              error:
                "Esta tarea es solo para alumnos de la clase. Inicia sesión en Edusico y únete con el código de clase (8 caracteres) en el panel de abajo.",
            }));
            return data;
          }
          const allowed = await checkClassroomMembership(data.classroomId, studentUid);
          if (!allowed) {
            setState((s) => ({
              ...s,
              isLoading: false,
              task: data,
              error:
                "Esta tarea es solo para tu clase. Ingresa el código de clase de 8 caracteres que te dio tu profesor y pulsa «Unirme» antes de empezar.",
            }));
            return data;
          }
        }

        if (studentUid && !isTaskAssignedToStudent(data, studentUid)) {
          setState((s) => ({
            ...s,
            isLoading: false,
            task: data,
            error: "Esta tarea fue asignada a otro estudiante de la clase.",
          }));
          return data;
        }

        setState((s) => ({ ...s, isLoading: false, task: data, error: null }));
        return data;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Error al cargar la tarea";
        setState((s) => ({ ...s, isLoading: false, error: msg }));
        return null;
      }
    },
    []
  );

  // Enviar resultado
  const submitResult = useCallback(async (
    taskId: string,
    teacherId: string,
    attempt: TaskAttempt,
    studentProfile?: StudentProfile | null
  ): Promise<boolean> => {
    setState((s) => ({ ...s, isSubmitting: true, error: null }));

    try {
      const task = state.task;
      if (task?.classroomId) {
        if (!studentProfile?.uid) {
          setState((s) => ({
            ...s,
            isSubmitting: false,
            error: "Esta tarea es solo para la clase. Inicia sesión con tu cuenta Edusico.",
          }));
          return false;
        }
        const allowed = await checkClassroomMembership(task.classroomId, studentProfile.uid);
        if (!allowed) {
          setState((s) => ({
            ...s,
            isSubmitting: false,
            error:
              "Esta tarea es solo para alumnos de la clase. Pide el código de clase a tu profesor y únete desde tu panel o al registrarte.",
          }));
          return false;
        }
      }

      if (task && studentProfile?.uid && !isTaskAssignedToStudent(task, studentProfile.uid)) {
        setState((s) => ({
          ...s,
          isSubmitting: false,
          error: "Esta tarea fue asignada a otro estudiante de la clase.",
        }));
        return false;
      }

      const resultId = `${taskId}_${studentProfile?.uid || "anon"}`;
      const resultRef = doc(db, "bordona_results", resultId);
      const prevSnap = await getDoc(resultRef);

      let result: TaskResult;
      const studentName = studentProfile
        ? `${studentProfile.firstName} ${studentProfile.lastName}`
        : "Estudiante anónimo";

      if (prevSnap.exists()) {
        const prev = prevSnap.data() as TaskResult;
        const attempts = [...prev.attempts, attempt];
        const bestScore = Math.max(prev.bestScore, attempt.score);
        result = {
          ...prev,
          teacherId,
          attempts,
          bestScore,
          completedAt: Date.now(),
          durationMs: prev.durationMs + (attempt.timestamp - (prev.attempts[prev.attempts.length - 1]?.timestamp || attempt.timestamp)),
        };
      } else {
        result = {
          taskId,
          teacherId,
          studentName,
          attempts: [attempt],
          bestScore: attempt.score,
          completedAt: Date.now(),
          durationMs: 0,
        };
      }

      await setDoc(resultRef, {
        ...result,
        submittedAt: serverTimestamp(),
      });

      // Registrar estadísticas locales
      recordTaskCompletion(
        { taskId, title: state.task?.title || "Tarea", type: state.task?.type || "custom" },
        result.bestScore
      );

      setState((s) => ({ ...s, isSubmitting: false, result }));
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al guardar el resultado";
      setState((s) => ({ ...s, isSubmitting: false, error: msg }));
      return false;
    }
  }, [state.task, recordTaskCompletion]);

  const reset = useCallback(() => {
    setState({ isLoading: false, isSubmitting: false, error: null, task: null, result: null });
  }, []);

  return { state, loadTask, submitResult, reset };
}
